import React, { useState, useEffect, useContext } from 'react';
import { useSKUTypeVsBrandVsSizeMap } from '../../hooks/services/caf';
import { CAFContextState, CAFContextValue } from '../../types/caf';
import GlobalContext from '../GlobalContext';
import { CAF_INITIALIZATION_SRC } from '../../config/caf';

export const CAFContext = React.createContext({} as CAFContextValue);
type Props = {
  children: React.ReactNode;
};

const initialState: CAFContextState = {
  sizeQuizInfo: null,
  isError: false,
  isLoading: false
};

export const CAFContextProvider = ({ children }: Props): JSX.Element => {
  const [cafContext, setCAFContext] = useState({ ...initialState });
  const { globalContext } = useContext(GlobalContext);
  const [enableSizeInfoFetching, setEnableSizeQuizFetching] = useState(false);
  const {
    isError, sizeQuizInfo, isFetched, isLoading
  } = useSKUTypeVsBrandVsSizeMap({ enabled: enableSizeInfoFetching });

  useEffect(() => {
    if (isError) {
      setCAFContext((prevContext) => ({ ...prevContext, isError: true, isLoading: false }));
    }

    if (isFetched) {
      setCAFContext((prevContext) => ({ ...prevContext, sizeQuizInfo: sizeQuizInfo, isLoading: false }));
    }
  }, [isFetched, isLoading]);

  useEffect(() => {
    if (enableSizeInfoFetching && !cafContext.sizeQuizInfo) {
      setEnableSizeQuizFetching(enableSizeInfoFetching);
    }
  }, [enableSizeInfoFetching]);

  const loadSizeQuizInfo = (event) => {
    const eventInfo = event.detail;

    setCAFContext((prevContext) => {
      if (!prevContext.sizeQuizInfo) {
        setEnableSizeQuizFetching(true);

        return ({
          ...prevContext, eventInfo: { ...eventInfo }, isLoading: true, openModalForLoader: true
        });
      }

      return ({ ...prevContext, isLoading: false, eventInfo: { ...eventInfo } });
    });
  };

  // opens CAF drawer for default
  useEffect(() => {
    if (globalContext.gb.utils.getFromStore('hasDefaultCAFDrawerShown') === 'true' ) {
      globalContext.gb.utils.removeFromStore('hasDefaultCAFDrawerShown');
      loadSizeQuizInfo({
        detail: {
          source: CAF_INITIALIZATION_SRC.AUTO,
          eventAction: 'eventAction',
          dispatchAction: 'initiate_conversation'
        }
      })
    }
  }, [])

  useEffect(() => {
    document.addEventListener('initiate_caf_flow', loadSizeQuizInfo);

    return (): void => {
      // remove event listener
      document.removeEventListener('initiate_caf_flow', loadSizeQuizInfo);
    };
  }, []);

  return (
    <CAFContext.Provider value = {{ cafContext: cafContext, setCAFContext: setCAFContext }}>
        {children}
    </CAFContext.Provider>
  );
};
